/**
 * UserMenu — header avatar trigger that opens a popover panel.
 *
 * Trigger: round avatar + caret button in the header right.
 * Panel:   user identity + theme picker + account nav links + sign out,
 *          positioned below the trigger via the Popover primitive (handles
 *          portal, click-outside, Escape, and focus management).
 */

import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { Popover } from '@/components/Popover/Popover';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import { logoutRequest } from '@/features/auth';
import { queryKeys } from '@/lib/query';
import { t } from '@/lib/i18n';
import { formatPersonName } from '@/lib/format';
import type { ThemeName } from '@/lib/theme';
import { ACCOUNT_NAV } from '../nav-config';
import styles from './UserMenu.module.css';

export function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const permissions = useAuthStore((s) => s.permissions);
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);

  const logoutMutation = useMutation({
    mutationFn: logoutRequest,
    onSettled: () => {
      clearSession();
      queryClient.removeQueries({ queryKey: queryKeys.auth.me() });
      void navigate('/login', { replace: true });
    },
  });

  const displayName = user ? formatPersonName(user) : '?';
  const displayInitials = initials(displayName);

  const visibleAccountNav = ACCOUNT_NAV.filter(
    (item) => !item.requiresPermission || permissions.has(item.requiresPermission),
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      trigger={
        <button
          type="button"
          className={styles.trigger}
          aria-label={t('Account menu')}
          data-testid="user-menu-trigger"
        >
          <span className={styles.avatar} aria-hidden="true">
            {displayInitials}
          </span>
          <span className={styles.caret} aria-hidden="true">
            ▾
          </span>
        </button>
      }
    >
      <div className={styles.panel} role="menu">
        <div className={styles.user}>
          <div className={styles.avatarLg} aria-hidden="true">
            {displayInitials}
          </div>
          <div className={styles.identity}>
            <div className={styles.name}>{user ? formatPersonName(user) : t('Signed in')}</div>
            <div className={styles.email}>{user?.email ?? ''}</div>
          </div>
        </div>

        <div className={styles.themeRow}>
          <ThemeOption value="light" current={theme} onSelect={setTheme} label={t('Light')} />
          <ThemeOption value="dark" current={theme} onSelect={setTheme} label={t('Dark')} />
          <ThemeOption value={null} current={theme} onSelect={setTheme} label={t('Auto')} />
        </div>

        {visibleAccountNav.length > 0 ? (
          <>
            <div className={styles.sep} aria-hidden="true" />
            <nav className={styles.navLinks} aria-label={t('Account navigation')}>
              {visibleAccountNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    [styles.navLink, isActive ? styles.navLinkActive : ''].filter(Boolean).join(' ')
                  }
                  onClick={() => setOpen(false)}
                >
                  {t(item.labelKey)}
                </NavLink>
              ))}
            </nav>
          </>
        ) : null}

        <div className={styles.sep} aria-hidden="true" />

        <Button
          variant="secondary"
          size="sm"
          onClick={() => logoutMutation.mutate()}
          isLoading={logoutMutation.isPending}
          data-testid="user-menu-sign-out"
        >
          {t('Sign out')}
        </Button>
      </div>
    </Popover>
  );
}

interface ThemeOptionProps {
  value: ThemeName | null;
  current: ThemeName | null;
  onSelect: (v: ThemeName | null) => void;
  label: string;
}

function ThemeOption({ value, current, onSelect, label }: ThemeOptionProps) {
  const isSelected = current === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      aria-pressed={isSelected}
      className={[styles.themeOption, isSelected ? styles.themeOptionActive : '']
        .filter(Boolean)
        .join(' ')}
    >
      {label}
    </button>
  );
}

function initials(source: string): string {
  const parts = source.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}
